import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Pencil, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/schema";
import { Cell } from "../components/Cell";
import RecordForm, { buildInitialValues, type FormValues } from "../components/RecordForm";
import RelatedTabs from "../components/RelatedTabs";
import type { ActionSchema, DetailResponse } from "../lib/types";
import { dateTime } from "../lib/format";

export default function ResourceDetail() {
  const { key, id } = useParams<{ key: string; id: string }>();
  const { resource, isLoading: schemaLoading, features } = useResource(key);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<ActionSchema | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["resource-detail", key, id],
    queryFn: () => api.get<DetailResponse>(`/superadmin/resources/${key}/${id}`),
    enabled: Boolean(key && id && resource),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["resource-detail", key, id] });
    queryClient.invalidateQueries({ queryKey: ["resource", key] });
    queryClient.invalidateQueries({ queryKey: ["activity"] });
  };

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      api.patch<DetailResponse>(`/superadmin/resources/${key}/${id}`, { values }),
    onSuccess: () => { setEditing(false); setError(null); invalidate(); },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Kunde inte spara"),
  });

  const runAction = useMutation({
    mutationFn: (action: ActionSchema) =>
      api.post<{ row: unknown }>(`/superadmin/resources/${key}/${id}/actions/${action.name}`),
    onSuccess: (res) => {
      setPendingAction(null); setError(null); invalidate();
      // A destroy action leaves nothing to show; go back to the list.
      if (!res.row) navigate(`/r/${key}`);
    },
    // The server refuses actions that would strand a customer (offboarding
    // guards, last-superadmin). Show that reason verbatim — it is the point.
    onError: (err) => setError(err instanceof ApiError ? err.message : "Åtgärden gick inte igenom"),
  });

  if (schemaLoading || isLoading) return <div className="panel h-96 animate-pulse" />;
  if (!resource) return <div className="panel p-6 text-sm text-ink-400">Okänd resurs: {key}</div>;
  if (!data) return <div className="panel p-6 text-sm text-ink-400">Hittades inte.</div>;

  const row = data.row;
  const title =
    (typeof row.name === "string" && row.name) ||
    (typeof row.title === "string" && row.title) ||
    (typeof row.business_name === "string" && row.business_name) ||
    `${resource.label} #${row.id}`;

  return (
    <div className="space-y-4">
      <Link to={`/r/${key}`} className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-300">
        <ArrowLeft className="h-3 w-3" /> {resource.label}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">{title}</h1>
          <p className="num text-sm text-ink-500">ID {row.id}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {resource.editable.length > 0 && !editing && (
            <button onClick={() => { setEditing(true); setError(null); }} className="btn-ghost">
              <Pencil className="h-3.5 w-3.5" /> Redigera
            </button>
          )}
          {resource.actions.map((action) => (
            <button
              key={action.name}
              onClick={() => (action.confirm ? setPendingAction(action) : runAction.mutate(action))}
              disabled={runAction.isPending}
              className={action.danger ? "btn-danger" : "btn-ghost"}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-200">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {editing ? (
        <div className="panel p-4">
          <RecordForm
            fields={resource.editable}
            columns={resource.detail_columns}
            required={resource.required}
            initial={buildInitialValues(resource.editable, resource.detail_columns, row)}
            geocoding={Boolean(features?.geocoding)}
            submitLabel="Spara"
            busy={save.isPending}
            onSubmit={(values) => save.mutate(values)}
            onCancel={() => { setEditing(false); setError(null); }}
          />
        </div>
      ) : (
        <RelatedTabs
          parentKey={resource.key}
          parentId={row.id}
          related={resource.related}
          overview={
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="panel p-4 lg:col-span-2">
                <dl className="divide-y divide-ink-850">
                  {resource.detail_columns.map((column) => (
                    <div key={column.key} className="grid grid-cols-3 gap-3 py-2 first:pt-0 last:pb-0">
                      <dt className="text-xs text-ink-500">{column.label}</dt>
                      <dd className="col-span-2 break-words text-sm text-ink-200">
                        {column.type === "text" && typeof row[column.key] === "string" ? (
                          <span className="whitespace-pre-wrap">{String(row[column.key])}</span>
                        ) : (
                          <Cell column={column} value={row[column.key]} />
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="panel p-4">
                <h2 className="mb-3 text-sm font-semibold text-white">Adminhistorik</h2>
                {data.audit.length === 0 ? (
                  <p className="text-sm text-ink-600">Ingen admin har rört den här posten.</p>
                ) : (
                  <ol className="space-y-2.5">
                    {data.audit.map((entry) => (
                      <li key={entry.id} className="border-l border-ink-700 pl-2.5">
                        <div className="text-xs text-ink-300">
                          <span className="font-medium">{entry.actor || "Okänd"}</span> · {entry.action}
                        </div>
                        <div className="num text-[10px] text-ink-600">{dateTime(entry.created_at)}</div>
                        {entry.details && Object.keys(entry.details).length > 0 && (
                          <pre className="mt-1 overflow-x-auto rounded bg-ink-850 p-1.5 text-[10px] text-ink-400">
                            {JSON.stringify(entry.details, null, 1)}
                          </pre>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          }
        />
      )}

      {pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="panel w-full max-w-md p-5">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${pendingAction.danger ? "text-red-400" : "text-amber-400"}`} />
              <h3 className="font-semibold text-white">{pendingAction.label}</h3>
            </div>
            <p className="mb-4 text-sm text-ink-400">{pendingAction.confirm}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPendingAction(null)} className="btn-ghost">Avbryt</button>
              <button
                onClick={() => runAction.mutate(pendingAction)}
                disabled={runAction.isPending}
                className={pendingAction.danger ? "btn-danger" : "btn-primary"}
              >
                {runAction.isPending ? "Kör…" : "Bekräfta"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
